import { type Ref, forwardRef } from 'react';
import { type FieldValues, type UseControllerProps, useController } from 'react-hook-form';

import getFieldError from '@/shared/form/helpers/getFieldError';
import useMapCustomValidators from '@/shared/form/hooks/useMapCustomValidators';
import { SwitchGroup } from '@/shared/form/inputs/SwitchGroup/SwitchGroup';
import type { InputProps } from '@/shared/form/types';

import { Switch as MantineSwitch, type SwitchProps as MantineSwitchProps } from '@mantine/core';
import { useMergedRef } from '@mantine/hooks';

export type SwitchProps<T extends FieldValues> = InputProps<UseControllerProps<T>> &
  Omit<MantineSwitchProps, 'value' | 'checked' | 'defaultValue' | 'onChange'>;

const Switch = forwardRef(function Switch<T extends FieldValues>(
  { name, control, defaultValue, shouldUnregister, onChange, ...props }: SwitchProps<T>,
  ref: Ref<HTMLInputElement>,
) {
  const rules = useMapCustomValidators(props, true);
  const {
    field: { value, onChange: fieldOnChange, ref: insideRef, ...field },

    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  const mergedRefs = useMergedRef(insideRef, ref);

  return (
    <MantineSwitch
      ref={mergedRefs}
      value={value}
      checked={value}
      onChange={e => {
        fieldOnChange(e);
        onChange?.(name, e.currentTarget.checked);
      }}
      error={getFieldError(fieldState)}
      mb="md"
      data-testid={name}
      {...field}
      {...props}
    />
  );
});

// @ts-ignore
Switch.Item = MantineSwitch;
// @ts-ignore
Switch.Group = SwitchGroup;

export default Switch;
